<script setup lang="ts">
import { useOIDCStore } from '@/stores/oidc.store'
import { storeToRefs } from 'pinia'
import { OrderBy } from '@/types/shared/filters'
import {
  SiteDataCenterTypeOptions,
  SiteArrangementOptions,
  SiteStatusOptions,
  SiteType,
  type FullSite,
} from '@/types/core/Site'
import type { ISiteFilters } from '@/types/core/filters'
import { getTenants } from '@/services/core.service'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { ElMessage } from 'element-plus'
import { computed, reactive, ref } from 'vue'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types'
import type { ITenant } from '@/types/core/Tenant'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { useSites } from '@/composables/core/sites'
import TableSearchFilter from '@/components/common/TableSearchFilter.vue'
import TableFilters from '@/components/common/TableFilters.vue'

const { getSitesQuery, deleteSiteMutation } = useSites()

const oidcStore = useOIDCStore()
const { isAdminUser } = storeToRefs(oidcStore)

const upsertDialogVisible = ref<boolean>(false)
const siteBeingModified = ref<FullSite | null>(null)

const tenantSearchLoading = ref<boolean>(false)
const tenantOptions = ref<OptionType[]>([])

const filters = reactive<ISiteFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  order_by: 'site_id',
  order_by_order: OrderBy.ASC,
  fragment: undefined,
  tenant_company_name: undefined,
  verified: undefined,
  type: undefined,
  data_center_type: undefined,
  arrangement: undefined,
  status: undefined,
})

const filterSettings = [
  {
    key: 'type',
    value: 'Type',
    options: [
      {
        key: SiteType.DataCenter,
        value: SiteType.DataCenter,
      },
      {
        key: SiteType.RepairCenter,
        value: SiteType.RepairCenter,
      },
      {
        key: SiteType.Other,
        value: SiteType.Other,
      },
    ],
    type: 'dropdown',
    placeholder: 'Type',
    modelValue: ref<string>(),
    onChange: (val: string) => {
      filters.type = val
    },
    iconClass: 'checklist',
  },
  {
    key: 'status',
    value: 'Status',
    options: SiteStatusOptions.map((opt: string) => {
      return {
        key: opt,
        value: opt,
      }
    }),
    type: 'dropdown',
    placeholder: 'Status',
    modelValue: ref<string>(),
    onChange: (val: string) => {
      filters.status = val
    },
    iconClass: 'label-outline-rounded',
  },
  {
    key: 'arrangement',
    value: 'Arrangement',
    options: SiteArrangementOptions.map((opt: string) => {
      return {
        key: opt,
        value: opt,
      }
    }),
    type: 'dropdown',
    placeholder: 'Arrangement',
    modelValue: ref<string>(),
    onChange: (val: string) => {
      filters.arrangement = val
    },
    iconClass: 'home-outline-rounded',
  },
  {
    key: 'data_center_type',
    value: 'Data Center Type',
    options: SiteDataCenterTypeOptions.map((opt: string) => {
      return {
        key: opt,
        value: opt,
      }
    }),
    type: 'dropdown',
    placeholder: 'Data Center Type',
    modelValue: ref<string>(),
    onChange: (val: string) => {
      filters.data_center_type = val
    },
    iconClass: 'dns-outline',
  },
]

const searchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'ID or Name',
  style: 'width: 232px',
  onChange: (val: string) => {
    if (val == '') {
      filters.fragment = undefined
    } else {
      filters.fragment = val
    }
  },
}

// Vue Query
const { isFetching, isError, isRefetching, data, error, refetch } = getSitesQuery(filters)

// Compute message to display when table is empty.
const tableEmptyText = computed(() => {
  if (isFetching.value && !filters.fragment) return 'Loading...'
  if (isFetching.value && filters.fragment) return `Searching for ${filters.fragment}`
  if (filters.fragment && !isFetching.value) return `No results for "${filters.fragment}"`
  else return 'No data'
})

/**
 * Handles edit of a visitor log (sets modifying state, opens dialog).
 *
 * @param visitorLog log to edit.
 */
const handleEdit = async (site: FullSite) => {
  siteBeingModified.value = site
  upsertDialogVisible.value = true
}

/**
 * Search tenants (el-select-v2 remote method).
 *
 * @param query search query fragment.
 */
const searchTenants = async (query: string) => {
  if (query.length < 3) {
    return
  }

  try {
    tenantSearchLoading.value = true
    const tenants = await getTenants({
      page: 1,
      per_page: DEFAULT_PAGE_SIZES[0],
      fragment: query,
    })
    tenantOptions.value = tenants.data.map((tenant: ITenant) => {
      return {
        label: tenant.company_name,
        value: tenant.company_name,
      }
    })
  } catch {
    ElMessage({
      type: 'error',
      message: 'Failed to search tenants.',
      grouping: true,
    })
  } finally {
    tenantSearchLoading.value = false
  }
}
</script>

<template>
  <ViewLayout class="sites">
    <template v-if="!isError" #controls>
      <el-button v-if="isAdminUser" type="primary" :disabled="isFetching" @click="upsertDialogVisible = true"
        >Create Site</el-button
      >
    </template>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="() => refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="sites__filters">
        <TableSearchFilter :search-settings="searchSettings" :disabled="isFetching" />
        <el-select-v2
          v-if="isAdminUser"
          v-model="filters.tenant_company_name"
          style="width: 232px"
          :options="tenantOptions"
          remote
          :remote-method="searchTenants"
          :loading="tenantSearchLoading"
          :disabled="isFetching"
          filterable
          clearable
          placeholder="Tenant"
        />
        <TableFilters :filters-list="filterSettings" />
        <el-checkbox v-model="filters.verified" label="Verified" />
      </div>
      <el-table v-loading="isFetching" :data="data?.data" :empty-text="tableEmptyText" stripe>
        <el-table-column prop="site_id" label="Site ID" :min-width="112" />
        <el-table-column prop="site_name" label="Name" :min-width="150" />
        <el-table-column prop="type" label="Type" :min-width="112" />
        <el-table-column label="Address" :min-width="210">
          <template #default="scope">
            <template v-if="scope.row.address">
              <p>{{ scope.row.address.address_1 ?? '' }}</p>
              <p v-if="scope.row.address.address_2">{{ scope.row.address.address_2 }}</p>
              <p>
                {{ `${scope.row.address.city}, ${scope.row.address.state}, ${scope.row.address.zip}` }}
              </p>
              <p>{{ scope.row.address.country }}</p>
            </template>
            <template v-else>
              {{ '-' }}
            </template>
          </template>
        </el-table-column>
        <el-table-column label="Facility Operator" :min-width="180">
          <template #default="scope">
            <p>{{ scope.row.operator_company ?? '' }}</p>
            <p>{{ scope.row.operator_name ?? '' }}</p>
            <p>{{ scope.row.operator_email ?? '' }}</p>
            <p>{{ scope.row.operator_phone ?? '' }}</p>
          </template>
        </el-table-column>
        <el-table-column label="Facility Owner" :min-width="180">
          <template #default="scope">
            <p>{{ scope.row.owner_company ?? '' }}</p>
            <p>{{ scope.row.owner_name ?? '' }}</p>
            <p>{{ scope.row.owner_email ?? '' }}</p>
            <p>{{ scope.row.owner_phone ?? '' }}</p>
          </template>
        </el-table-column>
        <el-table-column v-if="isAdminUser" :min-width="70" align="right">
          <template #default="scope">
            <el-button link title="Edit" @click="handleEdit(scope.row as FullSite)">
              <Icon icon="material-symbols:edit-outline-rounded" inline height="20" />
            </el-button>
            <el-button
              link
              title="Delete"
              @click="
                DeleteConfirmationModal(
                  'Delete Site',
                  'Are you sure you want to delete this site? This action cannot be undone',
                  scope.row.id,
                  deleteSiteMutation.mutateAsync
                )
              "
            >
              <Icon icon="material-symbols:delete-outline-rounded" inline height="20" />
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="data && data.total_pages > 0"
        v-model:current-page="filters.page"
        v-model:page-size="filters.per_page"
        class="sites__pagination"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        :disabled="isFetching"
        :layout="DEFAULT_PAGINATION_LAYOUT"
        background
      />
      <UpsertSiteDialog v-model="upsertDialogVisible" :site="siteBeingModified" @close="siteBeingModified = null" />
    </template>
  </ViewLayout>
</template>

<style scoped lang="scss">
.sites {
  &__filters {
    display: flex;
    gap: var(--spacing-spacing-sm);
  }

  &__pagination {
    align-self: flex-end;
  }
}
</style>
